/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from "@angular/core";
import swal, { SweetAlertIcon, SweetAlertOptions } from "sweetalert2";

@Injectable({
  providedIn: "root",
})
export class AlertService {
  readonly baseOptions: SweetAlertOptions = {
    width: 400,
    heightAuto: true,
    backdrop: true,
    animation: false,
  };

  showMessage2(
    title: string,
    text: any,
    icon: SweetAlertIcon,
    allowOutsideClick = true
  ): Promise<any> {
    let swalOpt: SweetAlertOptions = {
      showConfirmButton: true,
      width: 400,
      icon,
      title,
      text,
      backdrop: false,
    };

    swalOpt =
      icon === "success"
        ? {
            ...swalOpt,
            showConfirmButton: false,
            timer: 3000,
            toast: true,
            position: "top-right",
          }
        : { ...swalOpt, heightAuto: true, allowOutsideClick, backdrop: true };

    return swal.fire({ ...swalOpt });
  }

  showMessage(content: {
    title: string;
    text: string;
    icon: string;
    class: string;
    confirmButtonText?: string;
  }): Promise<any> {
    return swal.fire({
      ...this.baseOptions,
      iconHtml: `<span class="material-symbols-rounded  ${content.class}">${content.icon}</span>`,
      title: content.title,
      text: content.text,
      customClass: {
        actions: "mt-4",
        confirmButton: `btn btn-base mx-2 text-white btn-icon bg-base`,
      },
      confirmButtonText: content.confirmButtonText || "Cerrar",
    });
  }

  showMessageCustom(
    title: string,
    text: any,
    content: { icon: string; class: string }
  ): Promise<any> {
    return swal.fire({
      ...this.baseOptions,
      iconHtml: `<span class="material-symbols-rounded  ${content.class}">${content.icon}</span>`,
      title,
      text,
      allowOutsideClick: true,
      showConfirmButton: false,
      timer: 4000,
    });
  }

  showMessageCustomHTML(content: {
    title: string;
    text: string;
    icon: string;
    class: string;
    confirmButtonText?: string;
  }): Promise<any> {
    return swal.fire({
      ...this.baseOptions,
      iconHtml: `<span class="material-symbols-rounded ${content.class}">${content.icon}</span>`,
      title: content.title,
      html: content.text,
      customClass: {
        actions: "mt-4",
        confirmButton: `btn btn-base mx-2 text-white btn-icon bg-base`,
      },
      confirmButtonText: content.confirmButtonText || "Cerrar",
    });
  }

  confirm(
    mensaje: string | HTMLElement,
    type?: string,
    confirmButtonText?: string,
    title?: string,
    cancelButtonText = "Cancelar"
  ): Promise<any> {
    return swal.fire({
      title: title || "Advertencia",
      html: mensaje,
      icon: "warning",
      focusConfirm: false,
      focusCancel: false,
      showCancelButton: true,
      confirmButtonColor: this.getBtnConfirmColor(type),
      confirmButtonText,
      cancelButtonText,
      buttonsStyling: false,
      customClass: {
        actions: "mt-4 d-flex",
        confirmButton: `btn btn-${type || "danger"} mx-2 text-white btn-icon`,
        cancelButton: "btn btn-outline-secondary mx-2 btn-icon",
      },
      reverseButtons: true,
    });
  }

  confirmCustom(
    mensaje: string | HTMLElement,
    icon: string,
    type?: string,
    confirmButtonText?: string,
    title?: string,
    cancelButtonText = "Cancelar"
  ): Promise<any> {
    return swal.fire({
      ...this.baseOptions,
      title: title || "Advertencia",
      html: mensaje,
      iconHtml: `<span class="material-symbols-rounded">${icon}</span>`,
      focusConfirm: false,
      focusCancel: false,
      showCancelButton: true,
      confirmButtonColor: this.getBtnConfirmColor(type),
      confirmButtonText,
      cancelButtonText,
      buttonsStyling: false,
      customClass: {
        actions: "mt-4 d-flex",
        confirmButton: `btn btn-${type || "danger"} mx-2 text-white btn-icon`,
        cancelButton: "btn btn-outline-secondary mx-2 btn-icon",
      },
      reverseButtons: true,
    });
  }

  confirmation(
    options: SweetAlertOptions,
    icon?: string,
    type?: string
  ): Promise<any> {
    return swal.fire({
      iconHtml: `<span class="material-symbols-rounded ">${icon}</span>`,
      focusConfirm: false,
      focusCancel: false,
      showCancelButton: true,
      confirmButtonColor: this.getBtnConfirmColor(type),
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
      buttonsStyling: false,
      customClass: {
        actions: "mt-4",
        confirmButton: `btn btn-${type || "primary"} mx-2 text-white btn-icon`,
        cancelButton: "btn btn-outline-base mx-2 btn-icon",
      },
      reverseButtons: true,
      ...options,
    });
  }

  showPrompt(
    input: SweetAlertOptions["input"],
    title: string,
    text: string | HTMLElement,
    confirmButtonText: string,
    type?: string,
    cancelButtonText?: string
  ) {
    return swal.fire<string>({
      title,
      icon: "warning",
      html: text,
      input,
      inputPlaceholder: "Ingrese la observación",
      inputAttributes: {
        autocapitalize: "off",
      },
      customClass: {
        actions: "swal-actions",
        confirmButton: "swal-confirm-button text-white",
      },
      reverseButtons: true,
      confirmButtonColor: this.getBtnConfirmColor(type),
      confirmButtonText,
      cancelButtonText: cancelButtonText || "Cancelar",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: true,
      inputValidator: (value: any): Promise<string | false | void | null> => {
        return new Promise((resolve) => {
          if (value) {
            resolve(null);
          } else {
            resolve("Ingrese la observación");
          }
        });
      },
    });
  }

  getBtnConfirmColor(type?: string): string {
    switch (type) {
      case "success": {
        return "#26b539";
      }
      case "base": {
        return "#1141C7";
      }
      case "info": {
        return "#416cc9";
      }
      case "warning": {
        return "#ffd500";
      }
      case "secondary": {
        return "#e6ecf7";
      }
      case "primary": {
        return "#2f80ec";
      }
      case "danger": {
        return "#e04646";
      }
      default: {
        return "#2f80ec";
      }
    }
  }
}
